import { type ReactNode, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { PageTabsList } from "@volca/ui/components/ui/page-tabs-list";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { Text } from "@volca/ui/components/ui/text";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { cn } from "@volca/ui/lib/utils";
import { useTabNavigate } from "@/app-shell";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { WORKBOOK_FRAME } from "@/profitability/constants";
import { useReportOutput } from "@/profitability/query";
import { ReportWorkbook } from "@/profitability/report-workbook";
import { profitabilityRunPath } from "@/profitability/routes";
import type { ReportControlValues, ReportDates } from "@/profitability/types";
import type { ProfitabilityRun } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import type { Lens } from "./period-lens";
import { RUN_DOCUMENTS_TAB, runTabs } from "./report-panes";
import { ReportSkeleton } from "./report-skeleton";
import { ReportView } from "./report-view";
import { RunDocumentsTab } from "./run-documents-tab";

/** The grid of the same figures. Always last, after the report's own tabs. */

/**
 * A report's output, as the tabs the report asked for plus one the app always
 * adds.
 *
 * The strip lives here rather than in ReportView because the sheet is a
 * different component rather than another arrangement of blocks: the dashboard
 * and the grid are siblings, so the thing that switches between them sits above
 * both.
 */
export const InteractiveReport = ({
  businessId,
  runId,
  dates,
  sheet = false,
  run,
  onAskAiAboutCell,
  workbookTab = "workbook",
  documentsTab,
  paneInUrl = true,
  tabsInNav = false,
}: {
  workbookTab?: "sheet" | "workbook";
  documentsTab?: ReactNode;
  businessId: string;
  runId: string;
  dates: ReportDates | null;
  run?: Pick<
    ProfitabilityRun,
    | "workflow_version"
    | "id"
    | "documents"
    | "inputs"
    | "report"
    | "start_date"
    | "end_date"
    | "matched_columns"
    | "goal_document"
  >;
  /** Whether this run also has a workbook to show. */
  sheet?: boolean;
  /** Lets a cell of the grid start a question in the page's chat. */
  onAskAiAboutCell?: (context: AiScriptChatSelectionContext) => void;
  /** Whether the open tab lives in the run page's URL. Shown inside another
   *  page (the template workspace), the tab is held here instead, so
   *  switching tabs does not leave that page. */
  paneInUrl?: boolean;
  /** Whether the nav column lists this run's tabs (a ServiceTitan run's page). */
  tabsInNav?: boolean;
}) => {
  const output = useReportOutput(businessId, runId);
  const isMobile = useIsMobile();
  const tabNavigate = useTabNavigate();
  const { pane: routePane = null } = useParams<{ pane?: string }>();
  const [localPane, setLocalPane] = useState<string | null>(null);
  const chosen = paneInUrl ? routePane : localPane;
  const setChosen = (pane: string) => {
    if (!paneInUrl) return setLocalPane(pane);
    tabNavigate(`${profitabilityRunPath(runId)}/${encodeURIComponent(pane)}`);
  };
  // Held here rather than in ReportView, which unmounts when the reader opens
  // the grid. A filter they set is still set when they come back to a tab.
  const [values, setValues] = useState<ReportControlValues>({});
  const [lens, setLens] = useState<Lens>(null);

  if (output.isPending) return <ReportSkeleton />;
  if (output.isError)
    return (
      <Stack
        gap={2}
        align="center"
        justify="center"
        className="min-h-0 flex-1 px-6 py-8 text-center"
      >
        <Text variant="caption" role="alert" tone="destructive">
          {reportError(output.error)}
        </Text>
        <Button variant="secondary" onClick={() => void output.refetch()}>
          Retry report
        </Button>
      </Stack>
    );

  const tabs = runTabs({
    output: output.data,
    workbookTab,
    sheet: !!sheet,
    documents: !!run?.documents?.length,
  });
  const current =
    chosen && tabs.some((tab) => tab.id === chosen)
      ? chosen
      : (tabs[0]?.id ?? workbookTab);

  const onSheet = current === workbookTab;
  return (
    <Stack className="min-h-0 w-full flex-1" gap={0} align="stretch">
      {/* When the nav column lists the tabs, only a phone, whose column is
          in the drawer, keeps the strip. */}
      {tabs.length > 1 && (!tabsInNav || isMobile) && (
        <Tabs value={current} onValueChange={setChosen} className="pb-3">
          <PageTabsList>
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.title}
              </TabsTrigger>
            ))}
          </PageTabsList>
        </Tabs>
      )}
      {current === RUN_DOCUMENTS_TAB && run ? (
        (documentsTab ?? (
          <RunDocumentsTab
            businessId={businessId}
            run={run}
            output={output.data}
          />
        ))
      ) : onSheet ? (
        <Stack gap={0} align="stretch" className={cn("w-full", WORKBOOK_FRAME)}>
          <ReportWorkbook
            businessId={businessId}
            runId={runId}
            onAskAiAboutCell={onAskAiAboutCell}
          />
        </Stack>
      ) : (
        <ReportView
          key={runId}
          businessId={businessId}
          runId={runId}
          output={output.data}
          dates={dates}
          pane={current}
          onPaneChange={setChosen}
          values={values}
          onValuesChange={setValues}
          lens={lens}
          onLensChange={setLens}
        />
      )}
    </Stack>
  );
};
